import { collection, deleteDoc, doc, getDoc, getDocs, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { FIREBASE_COLLECTIONS } from "../config/firebaseCollections";
import { trackAppMetric } from "./appMetrics";

export const AUTOMATION_HEALTH_RETENTION_DAYS = 14;
export const AUTOMATION_HEALTH_RECENT_EVENT_LIMIT = 50;
export const AUTOMATION_HEALTH_FLUSH_DELAY_MS = 15000;

const AUTOMATION_HEALTH_PRUNE_MARKER_PREFIX = "automationHealthLastPruned";
const MAX_REASON_LENGTH = 64;
const VALID_OUTCOMES = new Set([
  "scheduled",
  "launched",
  "auto_closed",
  "manual_closed",
  "watchdog_closed",
  "missed",
  "blocked",
  "recovered",
  "skipped",
  "noop",
  "replaced",
  "pending",
  "playback_ok",
  "playback_failed",
]);
const VALID_STEP_TYPES = new Set(["music", "countdown", "reminder", "birthday", "resource", "wait", "unknown"]);

const pendingByDocKey = new Map();
let flushTimeoutId = null;
const prunePromisesByUid = new Map();

const toDateKey = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
};

const toIso = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
};

const sanitizeString = (value, maxLength = 160) =>
  String(value || "").trim().slice(0, maxLength);

const normalizeReason = (reason = "") => {
  const value = String(reason || "").toLowerCase();
  if (!value) return "unspecified";
  if (value.includes("watchdog")) return "watchdog_closed";
  if (value.includes("manual")) return "manual_closed";
  if (value.includes("four countdowns") || value.includes("four reminders")) return "max_instances";
  if (value.includes("multi") || value.includes("another lesson launchpad tab")) return "multi_tab";
  if (value.includes("active date")) return "active_date_mismatch";
  if (value.includes("paused")) return "automations_paused";
  if (value.includes("playlist")) return "playlist_not_found";
  if (value.includes("not enough time")) return "not_enough_time_remaining";
  if (value.includes("outside the catch-up window")) return "missed";
  if (value.includes("tab is inactive") || value.includes("tab hidden") || value.includes("browser tab")) {
    return "tab_hidden";
  }
  if (value.includes("no class in session")) return "no_class_at_custom_time";
  if (value.includes("no birthday")) return "no_birthday_students";
  if (value.includes("invalid")) return "invalid";
  if (value.includes("closed")) return "closed";
  if (value.includes("launched") || value.includes("sent to")) return "launched";
  if (value.includes("blocked audio") || value.includes("notallowed") || value.includes("user didn't interact")) {
    return "audio_play_not_allowed";
  }
  if (value.includes("zero volume") || value.includes("muted or set to zero")) return "volume_muted";
  if (value.includes("failed to load")) return "audio_load_error";
  if (value.includes("play() failed")) return "audio_play_failed";
  if (value.includes("youtube autoplay")) return "youtube_autoplay_blocked";
  if (value.includes("youtube player")) return "youtube_player_error";
  if (value.includes("track url is missing")) return "missing_url";
  if (value.includes("playback started")) return "playback_started";
  return value.replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, MAX_REASON_LENGTH) || "other";
};

const normalizeOutcome = (outcome = "") => {
  const normalized = sanitizeString(outcome, 48).toLowerCase();
  return VALID_OUTCOMES.has(normalized) ? normalized : "skipped";
};

const normalizeStepType = (stepType = "") => {
  const normalized = sanitizeString(stepType, 48).toLowerCase();
  return VALID_STEP_TYPES.has(normalized) ? normalized : "unknown";
};

const incrementCounter = (target, key, amount = 1) => {
  const counterKey = sanitizeString(key, 80) || "unknown";
  target[counterKey] = (Number(target[counterKey]) || 0) + amount;
};

const mergeCounters = (base = {}, increment = {}) => {
  const next = { ...(base || {}) };
  Object.entries(increment || {}).forEach(([key, value]) => {
    next[key] = (Number(next[key]) || 0) + (Number(value) || 0);
  });
  return next;
};

const getDailyDocRef = (uid, dateKey) =>
  doc(db, FIREBASE_COLLECTIONS.USERS, uid, FIREBASE_COLLECTIONS.AUTOMATION_HEALTH_DAILY, dateKey);

export const buildAutomationHealthPayload = ({
  uid,
  event = {},
  outcome,
  reason = "",
  details = {},
  timestamp = new Date(),
} = {}) => {
  const resolvedUid = sanitizeString(uid || auth.currentUser?.uid, 128);
  const dateKey = sanitizeString(event.dateKey || details.dateKey || toDateKey(timestamp), 10);
  const stepType = normalizeStepType(event.type || details.stepType);
  const normalizedOutcome = normalizeOutcome(outcome);
  const reasonCode = normalizeReason(details.reasonCode || reason);

  if (!resolvedUid || !dateKey) {
    return null;
  }

  return {
    uid: resolvedUid,
    dateKey,
    event: {
      eventKey: sanitizeString(event.key || details.eventKey, 240),
      sequenceId: sanitizeString(event.sequenceId || details.sequenceId, 128),
      stepId: sanitizeString(event.stepId || details.stepId, 128),
      classId: sanitizeString(event.classId || details.classId, 128),
      triggerMode: sanitizeString(event.triggerMode || details.triggerMode, 48),
      stepType,
      outcome: normalizedOutcome,
      reasonCode,
      scheduledStartTime: toIso(event.startTime || details.scheduledStartTime),
      scheduledEndTime: toIso(event.endTime || details.scheduledEndTime),
      occurredAt: toIso(timestamp),
      engagementSessionId: sanitizeString(details.engagementSessionId, 128),
      isPresentSurface: Boolean(details.isPresentSurface),
      isPresentVisible: Boolean(details.isPresentVisible),
      isPrimaryTab: details.isPrimaryTab === undefined ? null : Boolean(details.isPrimaryTab),
      runtimeTickGapMs: Math.max(0, Number(details.runtimeTickGapMs) || 0),
    },
    metricAttributes: {
      stepType,
      outcome: normalizedOutcome,
      reason: reasonCode,
      triggerMode: sanitizeString(event.triggerMode || details.triggerMode, 48) || "unknown",
      environment: process.env.REACT_APP_ENV || process.env.NODE_ENV || "unknown",
    },
  };
};

export const buildAutomationHealthDocPatch = (events = []) => {
  const counters = {
    total: 0,
    byOutcome: {},
    byStepType: {},
    byReason: {},
  };

  events.forEach((entry) => {
    counters.total += 1;
    incrementCounter(counters.byOutcome, entry.outcome);
    incrementCounter(counters.byStepType, entry.stepType);
    incrementCounter(counters.byReason, entry.reasonCode);
  });

  return { counters, recentEvents: events };
};

const enqueueDailyEvent = (payload) => {
  const docKey = `${payload.uid}|${payload.dateKey}`;
  const pending = pendingByDocKey.get(docKey) || {
    uid: payload.uid,
    dateKey: payload.dateKey,
    events: [],
  };
  pending.events.push(payload.event);
  pendingByDocKey.set(docKey, pending);
};

const scheduleFlush = (flushDelayMs = AUTOMATION_HEALTH_FLUSH_DELAY_MS) => {
  if (flushTimeoutId || typeof window === "undefined") return;
  flushTimeoutId = window.setTimeout(() => {
    flushTimeoutId = null;
    void flushAutomationHealthTelemetry();
  }, Math.max(0, Number(flushDelayMs) || 0));
};

export async function pruneAutomationHealthDaily(uid, { now = new Date(), force = false } = {}) {
  const resolvedUid = sanitizeString(uid || auth.currentUser?.uid, 128);
  if (!resolvedUid) return;

  const todayKey = toDateKey(now);
  const markerKey = `${AUTOMATION_HEALTH_PRUNE_MARKER_PREFIX}:${resolvedUid}`;
  if (!force && typeof localStorage !== "undefined" && localStorage.getItem(markerKey) === todayKey) {
    return;
  }
  if (prunePromisesByUid.has(resolvedUid)) {
    return prunePromisesByUid.get(resolvedUid);
  }

  const prunePromise = (async () => {
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - AUTOMATION_HEALTH_RETENTION_DAYS);
    const cutoffDateKey = toDateKey(cutoff);
    const healthCollection = collection(
      db,
      FIREBASE_COLLECTIONS.USERS,
      resolvedUid,
      FIREBASE_COLLECTIONS.AUTOMATION_HEALTH_DAILY
    );
    const snapshot = await getDocs(healthCollection);
    await Promise.all(
      snapshot.docs
        .filter((dailyDoc) => String(dailyDoc.id) < cutoffDateKey)
        .map((dailyDoc) => deleteDoc(dailyDoc.ref))
    );
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(markerKey, todayKey);
    }
  })().finally(() => {
    prunePromisesByUid.delete(resolvedUid);
  });

  prunePromisesByUid.set(resolvedUid, prunePromise);
  return prunePromise;
}

export async function flushAutomationHealthTelemetry() {
  if (flushTimeoutId && typeof window !== "undefined") {
    window.clearTimeout(flushTimeoutId);
    flushTimeoutId = null;
  }

  const pendingEntries = [...pendingByDocKey.values()];
  pendingByDocKey.clear();

  await Promise.all(pendingEntries.map(async (pending) => {
    const docRef = getDailyDocRef(pending.uid, pending.dateKey);
    const currentSnap = await getDoc(docRef);
    const current = currentSnap.exists() ? currentSnap.data() : {};
    const patch = buildAutomationHealthDocPatch(pending.events);
    const nextRecentEvents = [
      ...(patch.recentEvents || []),
      ...((current.recentEvents || []).slice(0, AUTOMATION_HEALTH_RECENT_EVENT_LIMIT)),
    ].slice(0, AUTOMATION_HEALTH_RECENT_EVENT_LIMIT);

    await setDoc(docRef, {
      uid: pending.uid,
      dateKey: pending.dateKey,
      retentionDays: AUTOMATION_HEALTH_RETENTION_DAYS,
      counters: {
        total: (Number(current?.counters?.total) || 0) + patch.counters.total,
        byOutcome: mergeCounters(current?.counters?.byOutcome, patch.counters.byOutcome),
        byStepType: mergeCounters(current?.counters?.byStepType, patch.counters.byStepType),
        byReason: mergeCounters(current?.counters?.byReason, patch.counters.byReason),
      },
      recentEvents: nextRecentEvents,
      updatedAt: serverTimestamp(),
      createdAt: current.createdAt || serverTimestamp(),
    }, { merge: true });

    await pruneAutomationHealthDaily(pending.uid);
  }));
}

export function trackAutomationHealthEvent(input = {}, options = {}) {
  const payload = buildAutomationHealthPayload(input);
  if (!payload) return null;

  trackAppMetric("automation.step_transition", payload.metricAttributes);
  if (payload.event.outcome === "watchdog_closed") {
    trackAppMetric("automation.watchdog_closed", {
      stepType: payload.metricAttributes.stepType,
      reason: payload.metricAttributes.reason,
      environment: payload.metricAttributes.environment,
    });
  }

  enqueueDailyEvent(payload);
  scheduleFlush(options.flushDelayMs);
  return payload;
}

export function resetAutomationHealthTelemetryForTests() {
  pendingByDocKey.clear();
  if (flushTimeoutId && typeof window !== "undefined") {
    window.clearTimeout(flushTimeoutId);
  }
  flushTimeoutId = null;
  prunePromisesByUid.clear();
}
